import { stripExtension } from '@/assets/js/Utilities';

export default class FileLoader {
	constructor() {
		this.activeMedia = {};
		this.previousMedia = {};
		this.sampleOrigCanvas;
		this.sampleCanvas;
		this.sampleOrigCtx;
		this.sampleCtx;
		this.img;
		this.imageName = "Original";
		this.firstImageLoaded = false;
		this.thumbnailImg;
		this.supportedFiletypes = {
			handleImageFile: ["png", "jpg", "jpeg", "bmp", "gif"],
			handleRawImageFile: [
				"cr3",
				"dc2",
				"rdc",
				"arw",
				"bay",
				"crw",
				"cr2",
				"cap",
				"dcs",
				"dcr",
				"dng",
				"drf",
				"eip",
				"erf",
				"fff",
				"iiq",
				"k25",
				"kdc",
				"mdc",
				"mef",
				"mos",
				"mrw",
				"nef",
				"nrw",
				"obm",
				"orf",
				"pef",
				"ptx",
				"pxn",
				"raf",
				"raw",
				"rwl",
				"rw2",
				"rwz",
				"sr2",
				"srf",
				"srw",
				"x3f",
				"3fr",
			],
			handleDPXImageFile: ["dpx"],
			handleTiffImageFile: ["tiff", "tif"],
			handleEXRImageFile: ["exr"],
			handleVideoFile: ["mp4"],
		};

		this.unsupportedFiletypes = {
			video: [
				"mov",
				"mxf",
				"avi",
				"webm",
				"mkv",
				"wmv",
				"amv",
				"mpg",
				"mpeg",
				"m4v",
			]
		};
	}

	getImageObject(imageURL) {
		return new Promise(resolve => {
			let img = new Image();
			img.crossOrigin = "Anonymous";
			img.src = imageURL;
			img.onload = () => {
				vm.$store.dispatch("Helpers/revokeObjectURL", imageURL);
				resolve(img);
			};
		});
	}

	// We are checking if all LUT files are loaded, so we can continue with the app.
	areRequiredFilesLoaded() {
		return new Promise(resolve => {
			if (luts.areRequiredLUTsLoaded()) resolve();
			else {
				luts.lutsLoadedInterval = setInterval(_ => {
					if (luts.areRequiredLUTsLoaded()) {
						clearInterval(luts.lutsLoadedInterval);
						resolve();
					}
				}, 200);
			}
		});
	}

	setActiveMediaURL() {
		if (this.isRawFile(this.activeMedia.type)) {
			this.activeMedia.url = URL.createObjectURL(this.activeMedia.file);
			return this.activeMedia.url;
		}

		this.activeMedia.url = URL.createObjectURL(this.activeMedia.file);
		return this.activeMedia.url;
	}

	revokeActiveMediaURL() {
		if (!this.activeMedia.url) return;

		URL.revokeObjectURL(this.activeMedia.url);
		this.activeMedia.url = null;
	}

	revokeActiveMediaRawDevelopedURL() {
		if (!this.activeMedia.rawDevelopment?.url) return;

		URL.revokeObjectURL(this.activeMedia.rawDevelopment.url);
		this.activeMedia.rawDevelopment.url = null;
	}

	resetActiveMediaRawDeveloped() {
		this.revokeActiveMediaRawDevelopedURL();

		this.activeMedia.rawDevelopment = {
			imageData: null,
			blob: null,
			type: null,
			url: null,
			values: null,
		}
	}

	resetActiveMedia() {
		this.revokeActiveMediaURL();
		this.revokeActiveMediaRawDevelopedURL();

		this.previousMedia = _.clone(this.activeMedia);

		this.activeMedia = {
			file: null,
			url: null,
			type: null,
			imageData: null,
			thumbnail: null,
			rawDevelopment: {
				imageData: null,
				blob: null,
				type: null,
				url: null,
				values: null
			},
			THREE: {
				texture: null
			}
		};
	}

	setActiveMediaForRaw(rawImageData) {
		// this.activeMedia.thumbnail = true;
		this.activeMedia.rawDevelopment.type = rawImageData.imageExtension;
		this.activeMedia.rawDevelopment.blob = rawImageData.imageBlob;
		this.activeMedia.rawDevelopment.url = URL.createObjectURL(rawImageData.imageBlob);
		this.activeMedia.rawDevelopment.imageData = rawImageData.imageData;
		this.activeMedia.rawDevelopment.values = rawImageData.values;
	}

	async setActiveMedia(file, fileType, imageId = false) {
		this.resetActiveMedia();
		this.removeVideoComponents();

		this.activeMedia.file = file;
		this.activeMedia.type = fileType;

		if (this.isRawFile(this.activeMedia.type)) {
			UI.hideLUTtemplates();
			if (!imageId) imageId = vm.$store.state.Projects.image;

			let rawImageData;
			let imageState = await window.lutify.fetchLastState(imageId);

			if (imageState?.rawDevelopment?.develop) {
				const developRawParemeters = vm.rawDevelopment.getDevelopParameters(imageState.rawDevelopment);
				rawImageData = await vm.rawDevelopment.getDevelopedImage(imageId, developRawParemeters);
			} else if (imageState?.rawDevelopment?.colourSpace) {
				const developRawParemeters = vm.rawDevelopment.getDevelopParameters({
					develop: true
				});
				rawImageData = await vm.rawDevelopment.getDevelopedImage(imageId, developRawParemeters);
			} else {
				rawImageData = await vm.rawDevelopment.getThumbnail(imageId);
			}

			this.setActiveMediaForRaw(rawImageData);

			vm.$store.dispatch('UI/setShowRawDevelopment', true);
		} else {
			vm.$store.dispatch('UI/setShowRawDevelopment', false);
		}

		this.setActiveMediaURL();
		return this.activeMedia;
	}

	async loadActiveMedia() {
		gpu.disableAllCreativePasses();

		// NOTE: we need to check if we can somehow refactor and merge gpu.initThreejs() and gpu.initNewImage()
		if (!this.firstImageLoaded) {
			this.firstImageLoaded = true;
			await gpu.initThreejs();
		} else {
			await gpu.initNewImage();
		}
	}

	removeVideoComponents() {
		$("#video").remove();
		vm.$store.dispatch("UI/setVideoLoaded", false);
		vm.$store.dispatch("UI/resetVideo");
	}

	async hasFileQuota(files) {
		let filesQuota = await vm.$store.dispatch("Projects/fetchFilesQuota");
		let uploadTotalSize = {
			bytes: 0,
			MB: 0,
		};
		files.forEach(file => (uploadTotalSize.bytes += file.size));
		uploadTotalSize.MB = (uploadTotalSize.bytes / 1000 / 1000).toFixed(2);

		if (filesQuota.data - uploadTotalSize.MB < 0) {
			window.vm.$snackbar(`You have reached your storage quota. Please upgrade your plan.`, "error", {
				timeout: -1,
			});
			$("#import-media").val("");
			$("#import-comment-media").val("");

			return false;
		} else return true;
	}

	async checkNumberOfUploadedImages(files) {
		if (vm.$store.state.User.account.plan_id !== 1) return true;

		await vm.$store.dispatch('User/fetchUser');

		if (vm.$store.state.User.account.number_of_uploaded_images + files.length > 100) {
			window.vm.$snackbar(`You have 100 uploads available on the free plan. <a href="account/change-plan" style="text-decoration: underline; color: white">Upgrade</a> to unlock unlimited uploads.`, "error", {
				timeout: -1,
			});
			$("#import-media").val("");
			$("#import-comment-media").val("");

			return false;
		} else return true;
	}

	filterUploadingFiles(files) {
		let supportedFiles = [];
		let unsupportedFiles = [];

		files.forEach(file => {
			let fileType = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
			if (this.isFiletypeSupported(fileType)) supportedFiles.push(file);
			else unsupportedFiles.push(file);
		});

		return { supportedFiles, unsupportedFiles };
	}

	filterCommentUploadingFiles(files) {
		let supportedFiles = [];
		let unsupportedFiles = [];

		files.forEach(file => {
			let fileType = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
			if (loader.supportedFiletypes.handleImageFile.includes(fileType)) supportedFiles.push(file);
			else unsupportedFiles.push(file);
		});

		return { supportedFiles, unsupportedFiles };
	}

	async uploadImages(commentMediaUpload = false, importList = null) {
		let filteredFiles;

		if (!importList) {
			if (!commentMediaUpload) importList = $("#import-media")[0].files;
			else importList = $("#import-comment-media")[0].files;
		}

		if (!commentMediaUpload) filteredFiles = this.filterUploadingFiles(importList);
		else filteredFiles = this.filterCommentUploadingFiles(importList);

		let selectedFiles = filteredFiles.supportedFiles;
		let failedFiles = filteredFiles.unsupportedFiles;

		$("#import-media").val("");
		$("#import-comment-media").val("");

		// Checking if we have any files to upload, after filtering them by filetype
		if (selectedFiles.length == 0) {
			if (failedFiles.length > 0) {
				vm.$store.dispatch("Projects/setFailedUploadsList", failedFiles);
				vm.$store.dispatch("Comments/uploadingAssetLoader", false);
				UI.showModal(UI.modals.files.failedProcessing);
			}

			return;
		}

		let totalUploadSize = 0;
		let uploadingFilesData = [];
		let uploadingFilesBatches = [];
		let project_id = vm.$store.state.Projects.project_id;
		let selectedFilesBatches = [];
		let batchMaxSize = 20;
		let batchList = [];

		await vm.$store.dispatch("Loader/showProgressBar", "indeterminate");

		// checking file quota
		if (!(await this.hasFileQuota(selectedFiles)) || !(await this.checkNumberOfUploadedImages(selectedFiles))) {
			await vm.$store.dispatch("Loader/showProgressBar", false);
			return;
		}

		vm.$store.dispatch("Projects/setUploadStatus", "processing");
		vm.$store.dispatch("Projects/setTotalUploadFiles", selectedFiles.length);
		// vm.$store.dispatch("Projects/resetProcessedFilesForUpload");

		// creating batches
		selectedFiles.forEach((file, key) => {
			if (batchList.length < batchMaxSize) {
				batchList.push(file);
			}

			if (batchList.length == batchMaxSize || key == selectedFiles.length - 1) {
				selectedFilesBatches.push(batchList);
				batchList = [];
			}
		});

		for await (const batch of selectedFilesBatches) {
			let uploadingFilesList = [];

			// Creating files
			for await (let file of batch) {
				let originalName = file.name.substring(0, file.name.lastIndexOf("."));
				let timestamp = Date.now();
				let newFilename = `${originalName}.${timestamp}`;
				let fileType = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
				let convertedImage;
				let thumbnailBlob;
				let rawPreview;

				// converting and renaming file
				try {
					convertedImage = await this.convertImage(file, fileType, newFilename);
					totalUploadSize += convertedImage.blob.size;
				} catch {
					failedFiles.push(file);
					vm.$store.dispatch("Projects/setTotalUploadFiles", selectedFiles.length - failedFiles.length);
					continue;
				}

				// for video and exr files, we use default thumbnail
				if (this.isVideoFile(fileType) || this.isEXRFile(fileType) || this.isTiffFile(fileType)) {
					thumbnailBlob = await this.fetchDefaultThumbnail();
				} else if (this.isRawFile(fileType)) {
					rawPreview = await vm.rawDevelopment.extractThumbnailFromRaw(convertedImage.blob);
					thumbnailBlob = rawPreview;
				} else thumbnailBlob = convertedImage.blob;

				// Compressing image to create thumbnail
				thumbnailBlob = await readAndCompressImage(thumbnailBlob, {
					quality: lutify.imageThumbnail.quality,
					maxWidth: lutify.imageThumbnail.maxWidth,
					maxHeight: lutify.imageThumbnail.maxHeight,
				});

				// renaming thumbnail blob file
				thumbnailBlob = new File([thumbnailBlob], `${originalName}.thumbnail.${timestamp}.jpg`);
				totalUploadSize += thumbnailBlob.size;

				if (commentMediaUpload) {
					let imageSize = await lutify.getImageSizeFromBlob(convertedImage.blob);
					if (imageSize.width > lutify.commentUpload.maxWidth || imageSize.height > lutify.commentUpload.maxWidth) {
						// Compressing image
						let compressedImage = await readAndCompressImage(convertedImage.blob, {
							quality: lutify.commentUpload.quality,
							maxWidth: lutify.commentUpload.maxWidth,
							maxHeight: lutify.commentUpload.maxHeight,
						});

						compressedImage = new File([compressedImage], `${originalName}.${timestamp}.jpg`);
						convertedImage.blob = compressedImage;
						convertedImage.type = 'jpg';
					}
				}

				uploadingFilesList.push({
					image_file: convertedImage.blob,
					name: convertedImage.blob.name,
					size: convertedImage.blob.size,
					sourceFileType: fileType,
					fileType: convertedImage.type,
					type: "image",
					thumbnail_file: thumbnailBlob,
					thumbnail_name: thumbnailBlob.name,
					thumbnail_size: thumbnailBlob.size,
					filePresignedUrl: null,
					file_id: null,
					thumbnailPresignedUrl: null,
					thumbnail_id: null,
					rawPreview,
					commentMediaUpload
				});
			}

			// getting signed urls and file_id's
			// NOTE check for failed case when quota is reached
			uploadingFilesData = await vm.$store.dispatch("Projects/postUploadingFilesData", uploadingFilesList);
			for await (const [key, uploadData] of uploadingFilesData.entries()) {
				uploadingFilesList[key].filePresignedUrl = uploadData.filePresignedUrl;
				uploadingFilesList[key].file_id = uploadData.file_id;
				uploadingFilesList[key].thumbnailPresignedUrl = uploadData.thumbnailPresignedUrl;
				uploadingFilesList[key].thumbnail_id = uploadData.thumbnail_id;

				// // storing image to indexedDB
				await uploadImagesStore.setItem(uploadingFilesList[key].file_id, {
					project_id,
					folder_id: vm.$store.state.Projects.action.id,
					id: uploadingFilesList[key].file_id,
					name: stripExtension(uploadingFilesList[key].name),
					sourceFileType: uploadingFilesList[key].sourceFileType,
					fileType: uploadingFilesList[key].fileType,
					type: this.isVideoFile(uploadingFilesList[key].fileType) ? "video" : "image",
					blob: uploadingFilesList[key].image_file,
					rawPreview: uploadingFilesList[key].rawPreview,
				});

				// // storing thumbnail to indexedDB
				await thumbnailsStore.setItem(uploadingFilesList[key].file_id, {
					project_id,
					folder_id: vm.$store.state.Projects.action.id,
					blob: uploadingFilesList[key].thumbnail_file,
					thumbnail_id: uploadingFilesList[key].thumbnail_id,
				});

				// vm.$store.dispatch("Projects/setProcessedFilesForUpload");
			}

			uploadingFilesBatches.push(...uploadingFilesList);

			await vm.$store.dispatch("Projects/refreshProjectTree");
			await vm.$store.dispatch("Projects/openProjectTree", [
				...vm.$store.state.Projects.openProjectTree,
				{ key: vm.$store.state.Projects.action.id + "-folder" },
			]);
		}

		// Creating, storing and posting project thumbnail from last image.
		// NOTE: only for non EXR files
		let lastSelectedFile = null;
		for (const [key, file] in selectedFiles) {
			if (failedFiles.map(e => e.name).includes(selectedFiles[selectedFiles.length - key - 1].name)) continue;
			else {
				lastSelectedFile = selectedFiles[selectedFiles.length - key - 1];
				break;
			}
		}

		if (lastSelectedFile && !commentMediaUpload) lutify.updateProjectThumbnail(lastSelectedFile, project_id, "convertImage");

		await vm.$store.dispatch("Loader/showProgressBar", "progress");
		await vm.$store.dispatch("Projects/updateTotalImagesUploadSize", totalUploadSize);
		await vm.$store.dispatch("Projects/resetTotalImagesUploaded"); // reset
		await vm.$store.dispatch("Projects/resetImageUploadResponses"); // reset
		vm.$store.dispatch("Projects/setUploadStatus", "uploading");
		vm.$store.dispatch("Projects/resetFilesUploaded");

		uploadingFilesBatches.forEach((fileData, key) => {
			vm.$store
				.dispatch("Projects/uploadToS3", {
					url: fileData.filePresignedUrl,
					file: fileData.image_file,
					key: fileData.file_id,
					uploadProgress: true,
				})
				.then(async () => {
					let imageStoreData = await uploadImagesStore.getItem(fileData.file_id);
					await uploadImagesStore.removeItem(fileData.file_id);
					await imagesStore.setItem(fileData.file_id, imageStoreData);
					vm.$store.dispatch("Projects/setFilesUploaded");

					vm.$store
						.dispatch("Projects/uploadToS3", {
							url: fileData.thumbnailPresignedUrl,
							file: fileData.thumbnail_file,
							key: fileData.thumbnail_id,
							uploadProgress: true,
						})
						.then(() => {
							vm.$store.dispatch("Projects/addImageUploadResponse", fileData.file_id);
							vm.$store.dispatch("Projects/addImageUploadResponse", fileData.thumbnail_id);
						});
				});
		});

		let interval = setInterval(async () => {
			if (vm.$store.state.Projects.imageUploadResponses.length == uploadingFilesBatches.length * 2) {
				clearInterval(interval);
				await vm.$store.dispatch("Projects/updateFilesUploadStatus", vm.$store.state.Projects.imageUploadResponses);
				vm.$store.dispatch("Projects/setUploadStatus", null);
				vm.$store.dispatch("Loader/showProgressBar", false);
				vm.$store.dispatch("Loader/setImportProgress", 0);
				vm.$store.dispatch("Projects/refreshProjectTree");

				if (commentMediaUpload) {
					let denotationChar = "#";
					let list = vm.$store.getters["Comments/hashValues"]('image');
					vm.$store.dispatch("Comments/uploadingAssetLoader", false);

					for await (const [key, uploadData] of uploadingFilesData.entries()) {
						let mention = list.find(value => value.id == uploadData.file_id);

						vm.$root.commentsEditor.$parent.insertMention(denotationChar, mention);
					}
				}

				if (failedFiles.length > 0) {
					vm.$store.dispatch(
						"Projects/setFailedUploadsList",
						failedFiles
					);
					UI.showModal(UI.modals.files.failedProcessing);
				}
			}
		}, 1000);
	}

	async fetchDefaultThumbnail() {
		let defaultsStoreData = await defaultsStore.getItem("file-thumbnail");

		if (!defaultsStoreData) {
			let response = await fetch("./assets/media/images/fylmai-site-logo-white-project.png");
			defaultsStoreData = await response.blob();
			await defaultsStore.setItem("file-thumbnail", defaultsStoreData);
		}

		defaultsStoreData.extension = '.png';
		return defaultsStoreData;
	}

	isBasicFileType(extension) {
		extension = extension.toLowerCase();

		if (this.supportedFiletypes.handleImageFile.includes(extension)) return true;
		else return false;
	}

	isVideoFile(extension) {
		return this.supportedFiletypes.handleVideoFile.includes(extension.toLowerCase());
	}

	isEXRFile(extension) {
		return this.supportedFiletypes.handleEXRImageFile.includes(extension.toLowerCase());
	}

	isRawFile(extension) {
		return this.supportedFiletypes.handleRawImageFile.includes(extension.toLowerCase());
	}

	isTiffFile(extension) {
		return this.supportedFiletypes.handleTiffImageFile.includes(extension.toLowerCase());
	}

	typeOfActiveMedia(type) {
		if (!this.activeMedia.type) return;

		let activeMediaExtension = this.activeMedia.type;
		if (type == 'exr') return this.isEXRFile(activeMediaExtension);
		if (type == 'raw') return this.isRawFile(activeMediaExtension);
		if (type == 'video') return this.isVideoFile(activeMediaExtension);
	}

	isFiletypeSupported(extension) {
		let supported = false;
		extension = extension.toLowerCase();

		for (const key in this.supportedFiletypes) {
			if (Object.prototype.hasOwnProperty.call(this.supportedFiletypes, key) && loader.supportedFiletypes[key].includes(extension)) supported = true;
		}

		return supported;
	}

	async convertImage(blob, extension, filename = "image") {
		extension = extension.toLowerCase();

		if (
			this.supportedFiletypes.handleImageFile.includes(extension) ||
			this.supportedFiletypes.handleEXRImageFile.includes(extension) ||
			this.supportedFiletypes.handleVideoFile.includes(extension) ||
			this.supportedFiletypes.handleRawImageFile.includes(extension) ||
			this.supportedFiletypes.handleTiffImageFile.includes(extension)
		) {
			blob = new File([blob], `${filename}.${extension}`, {
				type: blob.type,
				lastModified: blob.lastModified,
			});
			return { blob, type: extension };
		} else if (this.supportedFiletypes.handleDPXImageFile.includes(extension)) {
			let src = await this.convertDPXToSrc(blob);
			blob = this.dataURLtoFile(src, `${filename}.jpg`);
			return { blob, type: "jpg" };
		} else UI.showFileNotSupported();
	}

	convertDPXToSrc(file) {
		return new Promise(resolve => {
			var imgheader = file.slice(0, 8192);
			var img = file.slice(8192, file.size);

			var fr = new FileReader();
			fr.onloadend = function(evt) {
				if (evt.target.readyState == FileReader.DONE) {
					// DONE == 2
					var dpx = new DPXFile(evt.target.result);
					if (evt.target.result.length == 8192) {
						let imginfo = dpx.DPXImageInfo();

						UI.canvas.dpx.width = imginfo["width"];
						UI.canvas.dpx.height = imginfo["height"];

						fr.readAsBinaryString(img);
					} else {
						dpx.render10Bit("dpx-canvas", evt.target.result);
						resolve(UI.canvas.dpx.toDataURL("image/jpeg", 1));
					}
				}
			};

			fr.readAsBinaryString(imgheader);
		});
	}

	dataURLtoFile(dataurl, filename) {
		let arr = dataurl.split(","),
			mime = arr[0].match(/:(.*?);/)[1],
			bstr = atob(arr[1]),
			n = bstr.length,
			u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		return new File([u8arr], filename, { type: mime });
	}

	// dataURLtoBlob(dataurl) {
	// 	var arr = dataurl.split(","),
	// 		mime = arr[0].match(/:(.*?);/)[1],
	// 		bstr = atob(arr[1]),
	// 		n = bstr.length,
	// 		u8arr = new Uint8Array(n);
	// 	while (n--) {
	// 		u8arr[n] = bstr.charCodeAt(n);
	// 	}
	// 	return new Blob([u8arr], { type: mime });
	// }
}
